import React, {useEffect} from 'react';
import {Button, Col, DatePicker, Drawer, Form, Input, message, Row, Select, Space, Spin, Table} from 'antd';
import {ArrowDownOutlined, ArrowUpOutlined, CloseOutlined, DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import dayjs from 'dayjs';
import {PickupAppointmentType} from '../graphql/queries/getPickupAppointmentListing';
import {packageUnitFilterOptions} from '../types/PickupAppointmentType';
import PickupAppointmentAddModal from './PickupAppointmentAddModal';
import {usePickupTaskCreateDrawer} from '../hooks/usePickupTaskCreateDrawer';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import PointSelector from '../../pickup-task/components/PointSelector';
import {PickupTaskStartPointType} from '../../pickup-task/types/PickupTaskType';
import { useTranslation } from 'react-i18next';
import { PickupSourceEnum } from '../../../../enums/PickupSourceEnum';

interface PickupTaskCreateDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    selectedAppointmentIds?: number[];
    pickupDate?: string; // 格式：YYYY-MM-DD
    useSystemRouting?: boolean; // 是否使用系统排序
    taskSource?: PickupSourceEnum; // 创建任务来源
}

const PickupTaskCreateDrawer: React.FC<PickupTaskCreateDrawerProps> = ({
                                                                           visible,
                                                                           onClose,
                                                                           onSuccess,
                                                                           selectedAppointmentIds = [],
                                                                           pickupDate,
                                                                           useSystemRouting = true,
                                                                           taskSource
                                                                       }) => {
    const { currentWarehouse: warehouse } = useWarehouseContext();

    const {
        form,
        loading,
        appointmentList,
        addModalVisible,
        driverOptions,
        driversLoading,
        pickupPointOptions,
        pickupPointsLoading,
        destinationOptions,
        vehicleModelOptions,
        vehicleModelsLoading,
        vehicleOptions,
        vehiclesLoading,
        appointmentsLoading,
        summary,
        summaryUpdateKey,
        selectedVehicleModelId,
        selectedDestinationHub,
        handleSave,
        handleDriverChange,
        handleVehicleModelChange,
        handleOriginChange,
        handleDestinationChange,
        handleMoveUp,
        handleMoveDown,
        handleRemove,
        handleAddAppointment,
        handleAddAppointmentSuccess,
        setAddModalVisible,
        resetFormAndState,
        pickupDate: taskPickupDate,
        warehouseId
    } = usePickupTaskCreateDrawer({
        visible,
        onClose,
        onSuccess,
        selectedAppointmentIds,
        pickupDate,
        useSystemRouting,
        taskSource
    });

    const {t} = useTranslation();
    
    // 获取时间限制的辅助函数（简化版，不使用时区转换）
    const getTimeConstraints = () => {
        const taskDate = form.getFieldValue('taskDate');
       
        if (!taskDate) {
            return null;
        }
    
        // 获取pickup deadline time
        const pickupDeadlineTime = selectedDestinationHub?.pickupDeadlineTime || warehouse?.pickupDeadlineTime;

        // 用户选择的Task Date
        const taskDateLocal = dayjs(taskDate);

        // 直接基于taskDate创建开始和结束时间（不使用时区转换）
        // 当天开始时间 00:00
        const hubStart = taskDateLocal.clone().startOf('day');

        // pickupDeadlineTime，基于同一天
        let hubEnd = taskDateLocal.clone().endOf('day');
        if (pickupDeadlineTime) {
            // 处理时间格式，支持 "HH:mm" 或 "HH:mm:ss" 格式
            const timeParts = pickupDeadlineTime.split(':').map(Number);
            const hours = timeParts[0] || 0;
            const minutes = timeParts[1] || 0;
            const seconds = timeParts[2] || 0;
            hubEnd = taskDateLocal.clone().hour(hours).minute(minutes).second(seconds);
        } else {
            // 如果没有设置pickup deadline time，默认使用23:59:59
            hubEnd = taskDateLocal.clone().hour(23).minute(59).second(59);
        }

        return {
            hubStart,
            hubEnd,
            pickupDeadlineTime
        };
    };

    // 监听预约单列表变化，自动更新起点选择
    useEffect(() => {
        if (!appointmentList || appointmentList.length === 0) return;

        const currentStartPoint = form.getFieldValue('startPoint');

        // 如果起点已经选中了第一个预约单地址类型
        if (currentStartPoint && currentStartPoint.type === PickupTaskStartPointType.FIRST_APPOINTMENT) {
            const firstAppointment = appointmentList[0];

            // 检查是否需要更新（预约单ID发生变化）
            if (firstAppointment && firstAppointment.id.toString() !== currentStartPoint.value) {
                // 更新表单值，使用最新的第一个预约单信息
                form.setFieldsValue({
                    startPoint: {
                        type: PickupTaskStartPointType.FIRST_APPOINTMENT,
                        value: firstAppointment.id.toString(),
                        name: firstAppointment.addressLine1 || 'First Appointment Address'
                    }
                });
            }
        }
    }, [appointmentList, form]);
    
    // Table columns
    const columns: ColumnsType<PickupAppointmentType> = [


        {
            title: t('pickup.appointment.taskCreateDrawer.stop'),
            key: 'appointmentNumber',
            width: 120,
            render: (_, record) => (
                <span className="text-blue-600 font-medium">
                    {record.appointmentNumber}
                </span>
            )
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.zipcode'),
            dataIndex: 'zipcode',
            key: 'zipcode',
            width: 100,
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.pickupAddress'),
            dataIndex: 'addressLine1',
            key: 'addressLine1',
            width: 200,
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.quantity'),
            dataIndex: 'expectedParcelCount',
            key: 'expectedParcelCount',
            width: 70,
            align: 'left',
            render: (count) => count || 0
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.unit'),
            dataIndex: 'expectedParcelUnit',
            key: 'expectedParcelUnit',
            width: 70,
            align: 'left',
            render: (unit) => packageUnitFilterOptions.find(option => option.value === unit)?.label || unit
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.appointmentTime'),
            key: 'timeWindow',
            width: 120,
            render: (_, record) => `${record.expectedPickupTimeStart}-${record.expectedPickupTimeEnd}`
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.estDistance'),
            key: 'estimatedDistance',
            width: 100,
            align: 'left',
            render: () => (0.0).toFixed(1)
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.estDuration'),
            key: 'estimatedDuration',
            width: 100,
            align: 'left',
            render: () => (0.0).toFixed(1)
        },
        {
            title: '',
            key: 'actions',
            width: 120,
            render: (_, record, index) => {
                const isFirst = index === 0;
                const isLast = index === (appointmentList?.length || 0) - 1;

                return (
                    <Space size="small">
                        <Button
                            type="text"
                            icon={<ArrowUpOutlined/>}
                            onClick={() => handleMoveUp(record.id)}
                            className={isFirst ? "text-gray-300" : "text-blue-500"}
                            disabled={isFirst}
                        />
                        <Button
                            type="text"
                            icon={<ArrowDownOutlined/>}
                            onClick={() => handleMoveDown(record.id)}
                            className={isLast ? "text-gray-300" : "text-blue-500"}
                            disabled={isLast}
                        />
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined/>}
                            onClick={() => handleRemove(record.id)}
                        />
                    </Space>
                );
            },
        },
    ];

    return (
        <Drawer
            className="[&_.ant-form-item-label]:!text-left [&_.ant-form-item-label>label]:!justify-start [&_.ant-form-item-label>label]:!w-full"
            title={
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <span className="h-5 border-l-4 border-purple-500 mr-2 mt-1"></span>
                        <span className="text-xl font-medium text-black">{t('pickup.appointment.taskCreateDrawer.title')}</span>
                    </div>
                    <CloseOutlined onClick={() => {
                        resetFormAndState();
                        onClose();
                    }} className="cursor-pointer"/>
                </div>
            }
            width={1100}
            placement="right"
            closable={false}
            onClose={() => {
                resetFormAndState();
                onClose();
            }}
            open={visible}

            footer={
                <div className="flex justify-end gap-2">
                    <Button
                        type="primary"
                        onClick={() => handleSave(false)}
                        loading={loading}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('pickup.appointment.taskCreateDrawer.save')}</span>
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => handleSave(true)}
                        loading={loading}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('pickup.appointment.taskCreateDrawer.publish')}</span>
                    </Button>
                    <Button
                        onClick={() => {
                            resetFormAndState();
                            onClose();
                        }}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('pickup.appointment.taskCreateDrawer.discard')}</span>
                    </Button>
                </div>
            }
        >
            <Form
                form={form}
                layout="horizontal"
                className="mb-6"
                labelCol={{span: 8}}
                wrapperCol={{span: 16}}
            >
                <div className="border-b border-gray-200 pb-4 mb-4">
                    <Row gutter={[24, 16]}>
                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.appointment.taskCreateDrawer.taskNumber')}</span>}
                                name="taskNumber"
                                className="mb-2"
                            >
                                <Input placeholder="" disabled/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.appointment.taskCreateDrawer.taskStatus')}</span>}
                                name="status"
                                className="mb-2"
                            >
                                <Input value="DRAFT" disabled/>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.appointment.taskCreateDrawer.taskDate')}</span>}
                                name="taskDate"
                                rules={[{required: true, message: 'Please select task date'}]}
                                className="mb-2"
                            >
                                <DatePicker
                                    format="YYYY/MM/DD"
                                    className="w-full"
                                    disabled={true}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.appointment.taskCreateDrawer.driver')}</span>}
                                name="accountId"
                                rules={[{required: true, message: 'Please select driver'}]}
                                className="mb-2"
                            >
                                <Select
                                    className="w-full"
                                    loading={driversLoading}
                                    options={driverOptions.length > 0 ? driverOptions : []}
                                    placeholder={t('pickup.appointment.taskCreateDrawer.placeholders.selectDriver') || ''}
                                    onChange={handleDriverChange}
                                    showSearch
                                    optionFilterProp="label"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.appointment.taskCreateDrawer.origin')}</span>}
                                name="startPoint"
                                rules={[{
                                    required: true,
                                    message: t('pickup.appointment.taskCreateDrawer.messages.pleaseSelectOrigin') || '',
                                    validator: (_, value): Promise<void> => {
                                        if (value && value.type !== undefined && value.value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('pickup.appointment.taskCreateDrawer.messages.pleaseSelectOrigin') || ''));
                                    }
                                }]}
                                className="mb-2"
                            >
                                <PointSelector
                                    type="start"
                                    warehouseId={warehouseId || null}
                                    taskId={undefined}
                                    placeholder={t('pickup.appointment.taskCreateDrawer.placeholders.selectOrigin') || ''}
                                    customPointOptions={(() => {
                                        // 动态生成第一个预约单选项
                                        if (appointmentList && appointmentList.length > 0) {
                                            const firstAppointment = appointmentList[0];
                                            return {
                                                // 保留原有的hub和中继点选项，不要设为空数组
                                                hubList: pickupPointOptions.filter((p: any) => p.value.startsWith('1_')).map((p: any) => ({
                                                    id: p.value.split('_')[1],
                                                    name: p.label,
                                                    type: 'HUB',
                                                    value: p.value.split('_')[1],
                                                    fullAddress: p.address || '',
                                                    phone: ''
                                                })),
                                                rallyPointList: pickupPointOptions.filter((p: any) => p.value.startsWith('2_')).map((p: any) => ({
                                                    id: p.value.split('_')[1],
                                                    name: p.label,
                                                    type: 'RALLY_POINT',
                                                    value: p.value.split('_')[1],
                                                    fullAddress: p.address || '',
                                                    phone: ''
                                                })),
                                                firstAppointmentAddress: {
                                                    id: firstAppointment.id.toString(),
                                                    name: firstAppointment.addressLine1 || 'First Appointment Address', // 使用Pickup Address
                                                    type: 'FIRST_APPOINTMENT',
                                                    value: firstAppointment.id.toString(),
                                                    fullAddress: firstAppointment.addressLine1 || '',
                                                    phone: firstAppointment.contactName || ''
                                                },
                                                warehouseList: pickupPointOptions.filter((p: any) => p.value.startsWith('4_')).map((p: any) => ({
                                                    id: p.value.split('_')[1],
                                                    name: p.label,
                                                    type: 'WAREHOUSE',
                                                    value: p.value.split('_')[1],
                                                    fullAddress: p.address || '',
                                                    phone: ''
                                                })),
                                            };
                                        }
                                        return undefined;
                                    })()}
                                    onChange={(complexValue: string | null, option: any) => {
                                        if (!complexValue) {
                                            form.setFieldsValue({
                                                startPoint: undefined
                                            });
                                            return;
                                        }

                                        const [pointType, pointValue] = complexValue.split('-');
                                        const pointTypeInt = parseInt(pointType);

                                        let pointName = '';
                                        if (option?.children) {
                                            pointName = option.children;
                                        } else if (option?.['data-name']) {
                                            pointName = option['data-name'];
                                        }

                                        const startPointValue = {
                                            type: pointTypeInt,
                                            value: pointValue,
                                            name: pointName
                                        };

                                        form.setFieldsValue({
                                            startPoint: startPointValue
                                        });

                                        // 调用原有的handleOriginChange逻辑
                                        handleOriginChange(pointValue);

                                        setTimeout(() => {
                                            form.validateFields(['startPoint']);
                                        }, 0);
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.appointment.taskCreateDrawer.vehicleModel')}</span>}
                                name="vehicleTypeId"
                                rules={[{required: true, message: t('pickup.appointment.taskCreateDrawer.messages.pleaseSelectVehicleType') as string}]}
                                className="mb-2"
                            >
                                <Select
                                    className="w-full"
                                    loading={vehicleModelsLoading}
                                    options={vehicleModelOptions.length > 0 ? vehicleModelOptions : []}
                                    placeholder={t('pickup.appointment.taskCreateDrawer.placeholders.selectVehicleType') || ''}
                                    onChange={handleVehicleModelChange}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.appointment.taskCreateDrawer.destination')}</span>}
                                name="endPointValue"
                                rules={[{required: true, message: t('pickup.appointment.taskCreateDrawer.messages.pleaseSelectDestination') as string}]}
                                className="mb-2"
                            >
                                <Select
                                    className="w-full"
                                    loading={pickupPointsLoading}
                                    options={destinationOptions.length > 0 ? destinationOptions : []}
                                    placeholder={t('pickup.appointment.taskCreateDrawer.placeholders.selectDestination') || ''}
                                    onChange={async (value) => {
                                        // 调用原有的handleDestinationChange逻辑
                                        handleDestinationChange(value);

                                        // 如果选择了Destination，延迟一下再进行自动赋值，确保状态已更新
                                        if (value) {
                                            // 使用setTimeout确保selectedDestinationHub状态已更新
                                            setTimeout(() => {
                                                const taskDate = form.getFieldValue('taskDate');
                                                if (taskDate) {
                                                    // 获取最新的constraints，此时selectedDestinationHub应该已经更新
                                                    const constraints = getTimeConstraints();

                                                    if (constraints) {
                                                        // Expected Departure Time默认值：当天00:00
                                                        const defaultDepartureTime = dayjs(taskDate).startOf('day');

                                                        // Delivery Deadline Time默认值：根据pickup deadline time或23:59
                                                        const defaultDeadlineTime = constraints.hubEnd;

                                                        form.setFieldsValue({
                                                            expDepartureTime: defaultDepartureTime,
                                                            expCompleteTime: defaultDeadlineTime
                                                        });
                                                    }
                                                }
                                            }, 100); // 延迟100ms确保状态更新
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.appointment.taskCreateDrawer.vehicle')}</span>}
                                name="vehicleId"
                                rules={[{required: true, message: t('pickup.appointment.taskCreateDrawer.messages.pleaseSelectVehicle') as string}]}
                                className="mb-2"
                            >
                                <Select
                                    className="w-full"
                                    loading={vehiclesLoading}
                                    options={vehicleOptions.length > 0 ? vehicleOptions : []}
                                    placeholder={selectedVehicleModelId ? t('pickup.appointment.taskCreateDrawer.placeholders.selectVehicle') as string : t('pickup.appointment.taskCreateDrawer.placeholders.pleaseSelectVehicleTypeFirst') as string}
                                    disabled={!selectedVehicleModelId}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label={
                                    <span className="text-gray-500 break-words whitespace-normal">
                                      {t('pickup.appointment.taskCreateDrawer.expectedDepartureTime')}
                                    </span>
                                }
                                name="expDepartureTime"
                                rules={[
                                    {required: true, message: t('pickup.appointment.taskCreateDrawer.messages.pleaseSelectExpectedDepartureTime') || ''},
                                ]}
                                dependencies={['taskDate', 'endPointValue']}
                                className="mb-2"
                            >
                                <DatePicker
                                    showTime
                                    format="YYYY/MM/DD HH:mm"
                                    className="w-full"
                                    disabledDate={(current) => {
                                        const taskDate = form.getFieldValue('taskDate');
                                        if (!taskDate || !current) return false;
                                        // 直接比较日期，不使用时区转换
                                        return !current.isSame(taskDate, 'day');
                                    }}
                                    disabledTime={() => {
                                        // Expected Departure Time 可以选择当天的任何时间（00:00-23:59）
                                        return {};
                                    }}
                                    onChange={() => {
                                        const expCompleteTime = form.getFieldValue('expCompleteTime');
                                        if (expCompleteTime) {
                                            form.validateFields(['expCompleteTime']);
                                        }
                                    }}
                                />
                            </Form.Item>

                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500 whitespace-nowrap">{t('pickup.appointment.taskCreateDrawer.actualDepartureTime')}</span>}
                                className="mb-2"
                            >
                                <span className="text-gray-400">-</span>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label={
                                    <span className="text-gray-500 break-words whitespace-normal">
                                      {t('pickup.appointment.taskCreateDrawer.deliveryDeadlineTime')}
                                    </span>
                                }
                                name="expCompleteTime"
                                rules={[
                                    {required: true, message: t('pickup.appointment.taskCreateDrawer.messages.pleaseSelectDeliveryDeadline') || ''},
                                    ({getFieldValue}) => ({
                                        validator(_, value) {
                                            if (!value) return Promise.resolve();

                                            const expDepartureTime = getFieldValue('expDepartureTime');
                                            const constraints = getTimeConstraints();

                                            // 检查是否在Expected Departure Time之后
                                            if (expDepartureTime && value.isBefore(expDepartureTime)) {
                                                return Promise.reject(new Error(t('pickup.appointment.taskCreateDrawer.messages.deliveryDeadlineMustBeAfterExpectedDepartureTime') || ''));
                                            }

                                            // 检查是否在pickupDeadlineTime之前
                                            if (constraints?.hubEnd && value.isAfter(constraints.hubEnd)) {
                                                const errorMessage = constraints.pickupDeadlineTime
                                                    ? t('pickup.appointment.taskCreateDrawer.messages.deliveryDeadlineMustBeBeforeEndTime', {hubEnd: constraints.hubEnd.format('HH:mm')}) || ''
                                                    : t('pickup.appointment.taskCreateDrawer.messages.deliveryDeadlineMustBeBeforeEndTime', {hubEnd: constraints.hubEnd.format('HH:mm')}) || '';
                                                return Promise.reject(new Error(errorMessage));
                                            }

                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                                dependencies={['expDepartureTime', 'taskDate']}
                                className="mb-2"
                            >
                                <DatePicker
                                    showTime
                                    format="YYYY/MM/DD HH:mm"
                                    className="w-full"
                                    disabledDate={(current) => {
                                        const taskDate = form.getFieldValue('taskDate');
                                        if (!taskDate || !current) return false;
                                        // 直接比较日期，不使用时区转换
                                        return !current.isSame(taskDate, 'day');
                                    }}
                                    disabledTime={(current) => {
                                        if (!current) return {};
                                        const taskDate = form.getFieldValue('taskDate');
                                        if (!taskDate) return {};
                                        
                                        // 如果不是当天，禁用所有时间
                                        if (!current.isSame(taskDate, 'day')) {
                                            return {
                                                disabledHours: () => Array.from({length: 24}, (_, i) => i),
                                                disabledMinutes: () => Array.from({length: 60}, (_, i) => i),
                                                disabledSeconds: () => Array.from({length: 60}, (_, i) => i)
                                            };
                                        }
                                        
                                        // 如果是当天，根据pickupDeadlineTime限制
                                        const constraints = getTimeConstraints();
                                        if (!constraints) return {};
                                        
                                        const {pickupDeadlineTime} = constraints;
                                        if (pickupDeadlineTime) {
                                            const timeParts = pickupDeadlineTime.split(':').map(Number);
                                            const deadlineHour = timeParts[0] || 0;
                                            const deadlineMinute = timeParts[1] || 0;
                                            
                                            return {
                                                disabledHours: () => {
                                                    const hours = [];
                                                    // 禁用截止时间之后的小时
                                                    for (let i = deadlineHour + 1; i < 24; i++) {
                                                        hours.push(i);
                                                    }
                                                    return hours;
                                                },
                                                disabledMinutes: (selectedHour) => {
                                                    const minutes = [];
                                                    // 如果选择的是截止小时，禁用截止分钟之后的分钟
                                                    if (selectedHour === deadlineHour) {
                                                        for (let i = deadlineMinute + 1; i < 60; i++) {
                                                            minutes.push(i);
                                                        }
                                                    }
                                                    return minutes;
                                                }
                                            };
                                        }
                                        
                                        // 如果没有pickup deadline time，允许所有时间（00:00-23:59）
                                        return {};
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500 whitespace-nowrap">{t('pickup.appointment.taskCreateDrawer.actualCompletionTime')}</span>}
                                className="mb-2"
                            >
                                <span className="text-gray-400">-</span>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            </Form>

            <div className="bg-gray-50 p-2 mb-4 flex justify-between items-center">
                <div className="flex space-x-4 text-sm">
                    <div>{t('pickup.appointment.taskCreateDrawer.appointments')}: <span className="font-medium">{summary.appointmentCount}</span></div>
                    <div>{t('pickup.appointment.taskCreateDrawer.quantity')}: <span className="font-medium">{summary.quantity}</span></div>
                    <div>{t('pickup.appointment.taskCreateDrawer.estDistance')}: <span className="font-medium">{summary.distance}</span></div>
                    <div>{t('pickup.appointment.taskCreateDrawer.estDuration')}: <span className="font-medium">{summary.duration}</span></div>
                </div>
                <Button type="link" className="text-purple-500 font-medium" onClick={() => {
                    // Logic to update estimated data can be added here
                    message.info('Updating estimated data...');
                }}>{t('pickup.appointment.taskCreateDrawer.updateEstimatedData')}</Button>
            </div>

            {appointmentsLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Spin size="large" tip="Loading appointments..."/>
                </div>
            ) : (
                <Table
                    key={summaryUpdateKey}
                    columns={columns}
                    dataSource={appointmentList || []}
                    pagination={false}
                    size="small"
                    className="mb-4"
                    rowClassName="text-sm"
                    rowKey="id"
                    footer={() => (
                        <div className="flex justify-center">
                            <Button
                                type="link"
                                icon={<PlusOutlined/>}
                                onClick={handleAddAppointment}
                                className="text-purple-500"
                            >
                                {t('pickup.appointment.taskCreateDrawer.add')}
                            </Button>
                        </div>
                    )}
                    summary={() => {
                        // Get destination information
                        const endPointValue = form.getFieldValue('endPointValue');
                        const selectedEndPoint = endPointValue ?
                            destinationOptions.find((p: { value: string }) => p.value === endPointValue) : null;

                        return (
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0}>Destination</Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>
                                    {selectedEndPoint ? selectedEndPoint.zipcode : '--'}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2}>
                                    {selectedEndPoint ? selectedEndPoint.address : '--'}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3}>-</Table.Summary.Cell>
                                <Table.Summary.Cell index={4}>-</Table.Summary.Cell>
                                <Table.Summary.Cell index={5}>
                                    {selectedDestinationHub?.pickupDeadlineTime ?
                                        selectedDestinationHub.pickupDeadlineTime.substring(0, 5) : '--'}

                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6}>
                                    {selectedEndPoint ? '0.0' : '--'}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={7}>
                                    {selectedEndPoint ? '0.0' : '--'}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={8}></Table.Summary.Cell>
                            </Table.Summary.Row>
                        );
                    }}
                />
            )}
            <PickupAppointmentAddModal
                visible={addModalVisible}
                onClose={() => setAddModalVisible(false)}
                onAdd={handleAddAppointmentSuccess}
                disabledIds={appointmentList.map((item: PickupAppointmentType) => item.id)}
                pickupDate={taskPickupDate}
            />
        </Drawer>
    );
};

export default PickupTaskCreateDrawer; 